import { useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
  ArrowLeftIcon,
  PencilIcon,
  FolderIcon,
  CubeIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { GET_CATEGORY } from '../graphql/product-queries'
import { REMOVE_CATEGORY_FROM_PRODUCT } from '../graphql/product-mutations'
import LoadingSpinner from '../components/LoadingSpinner'
import AddProductsModal from '../components/AddProductsModal'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'

const CategoryDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const { hasPermission } = usePermissions()

  const canUpdate = hasPermission(PERMISSIONS.CATEGORY_UPDATE)

  const [addOpen, setAddOpen] = useState(false)
  const [removingProductId, setRemovingProductId] = useState<number | null>(null)

  const { data: categoryData, loading: categoryLoading, error: categoryError } = useQuery(GET_CATEGORY, {
    variables: { id: parseInt(id!) },
    skip: !id
  })

  const [removeCategoryFromProduct] = useMutation(REMOVE_CATEGORY_FROM_PRODUCT, {
    refetchQueries: id ? [{ query: GET_CATEGORY, variables: { id: parseInt(id) } }] : [],
    awaitRefetchQueries: true,
  })

  const handleRemove = async (productId: number, productName: string) => {
    if (!id) return
    if (!window.confirm(`Remove "${productName}" from this category?`)) return
    setRemovingProductId(productId)
    try {
      await removeCategoryFromProduct({
        variables: { productId, categoryId: parseInt(id) },
      })
      toast.success('Product removed from category')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove product')
    } finally {
      setRemovingProductId(null)
    }
  }

  const category = categoryData?.category

  if (categoryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (categoryError || !category) {
    return <Navigate to="/categories" replace />
  }

  // Filter only active products
  const activeProducts = category.products?.filter((product: any) => product.active) || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/categories"
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{category.name}</h1>
            <p className="text-sm text-gray-500">Category Details</p>
          </div>
        </div>
        {canUpdate && (
          <Link
            to={`/categories/${category.id}/edit`}
            className="btn-secondary"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </Link>
        )}
      </div>

      {/* Category Info - Moved to top for better user flow */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info - Now first for immediate context */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Name</label>
                <p className="mt-1 text-sm text-gray-900">{category.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Parent Category</label>
                <p className="mt-1 text-sm text-gray-900">
                  {category.parent?.name || 'No parent category'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Subcategories</label>
                <p className="mt-1 text-sm text-gray-900">{category.children?.length || 0}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Total Products</label>
                <p className="mt-1 text-sm text-gray-900">{category.products?.length || 0}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Visibility</label>
                <div className="mt-1 flex gap-2">
                  {category.display_in_pos && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      POS
                    </span>
                  )}
                  {category.display_in_webshop && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                      Webshop
                    </span>
                  )}
                  {!category.display_in_pos && !category.display_in_webshop && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Hidden
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Category Information - Subcategories second */}
          {category.children && category.children.length > 0 && (
            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Subcategories</h2>
              <div className="grid grid-cols-1 gap-3">
                {category.children.map((child: any) => (
                  <Link
                    key={child.id}
                    to={`/categories/${child.id}`}
                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <FolderIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">{child.name}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Total Products</span>
                <span className="text-sm font-medium text-gray-900">{category.products?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Active Products</span>
                <span className="text-sm font-medium text-gray-900">{activeProducts.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Subcategories</span>
                <span className="text-sm font-medium text-gray-900">{category.children?.length || 0}</span>
              </div>
            </div>
          </div>

          {/* Empty States */}
          {(!category.children || category.children.length === 0) && (
            <div className="card">
              <div className="text-center py-6">
                <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No subcategories</h3>
                <p className="mt-1 text-sm text-gray-500">This category has no subcategories.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Products Section - Moved to bottom for better user flow */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Products in this Category</h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {activeProducts.length} active product{activeProducts.length !== 1 ? 's' : ''}
            </span>
            {canUpdate && (
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add products
              </button>
            )}
          </div>
        </div>

        {activeProducts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeProducts.map((product: any) => (
              <div
                key={product.id}
                className="relative border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
              >
                {canUpdate && (
                  <button
                    type="button"
                    onClick={() => handleRemove(product.id, product.name)}
                    disabled={removingProductId === product.id}
                    title="Remove from category"
                    aria-label={`Remove ${product.name} from category`}
                    className="absolute top-2 right-2 z-10 p-1 rounded-full bg-white/90 text-gray-500 hover:text-red-600 hover:bg-white shadow-sm disabled:opacity-50"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
                {/* Product Image */}
                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <CubeIcon className="h-16 w-16 text-gray-400" />
                  )}
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-gray-900 text-lg line-clamp-2">
                      {product.name}
                    </h3>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {product.kind}
                    </span>
                  </div>

                  {product.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {product.description}
                    </p>
                  )}

                  {/* Product Variants */}
                  {product.variants && product.variants.length > 0 && (
                    <div className="space-y-1 mb-3">
                      {product.variants.slice(0, 2).map((variant: any) => (
                        <div key={variant.id} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{variant.name}</span>
                          <span className="font-medium text-gray-900">
                            ${(variant.price_cents / 100).toFixed(2)}
                          </span>
                        </div>
                      ))}
                      {product.variants.length > 2 && (
                        <p className="text-xs text-gray-500">
                          +{product.variants.length - 2} more variants
                        </p>
                      )}
                    </div>
                  )}

                  <Link
                    to={`/products/${product.id}`}
                    className="block w-full text-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <CubeIcon className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No active products</h3>
            <p className="mt-2 text-sm text-gray-500">
              This category doesn't have any active products yet.
            </p>
            {canUpdate && (
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 transition-colors"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add products
              </button>
            )}
          </div>
        )}
      </div>

      {canUpdate && id && (
        <AddProductsModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          categoryId={parseInt(id)}
          currentProductIds={new Set<number>((category.products ?? []).map((p: any) => p.id))}
        />
      )}
    </div>
  )
}

export default CategoryDetailPage
